import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import axios from '../config/api';


const Layout = ({ children }) => {
  const { user, isAuthenticated, loading, logout, updateUser } = useAuth(); // Changed from refreshUser to updateUser
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { success, error } = useNotification();
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  const navigationItems = [
    { name: 'Dashboard', href: '/dashboard', icon: '🏠' },
    { name: 'My Keys', href: '/my-keys', icon: '🔑' },
    { name: 'Purchase', href: '/purchase', icon: '🛒' },
    { name: 'Products', href: '/products', icon: '📦' },
    { name: 'Exchange', href: '/exchange', icon: '💱' },
    ...(user?.role === 'owner' ? [
      { name: 'Admin Panel', href: '/admin', icon: '⚙️' }
    ] : []),
    ...(user?.role === 'reseller' ? [
      { name: 'Resseler Panel', href: '/admin', icon: '⚙️' },
    ] : [])
  ];

  // Close sidebar on navigation
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location]);

  // User profile refresh interval
  useEffect(() => {
    if (isAuthenticated) {
      const interval = setInterval(async () => {
        try {
          const response = await axios.get('/api/users/profile');
          updateUser(response.data); // Changed from refreshUser to updateUser
        } catch (err) {
          console.error('Failed to refresh user data:', err);
        }
      }, 60000); // Refresh every minute

      return () => clearInterval(interval);
    }
  }, [isAuthenticated, updateUser]); // Changed from refreshUser to updateUser

  // Add a manual refresh function that can be triggered
  const handleBalanceRefresh = async () => {
    try {
      const response = await axios.get('/api/users/profile');
      updateUser(response.data); // Changed from refreshUser to updateUser
      success('Balance refreshed successfully!');
    } catch (error) {
      success('Failed to refresh balance');
    }
  };

    
  return (
    <div className="min-h-screen bg-gradient-nightcore">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-dark-100 border-r border-purple-500/20 transform ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out lg:translate-x-0`}>
        <div className="flex items-center justify-center p-6 border-b border-purple-500/20">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
            AI Panel
          </h1>
        </div>
        
        <nav className="mt-6">
          {navigationItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className={`flex items-center px-6 py-3 text-sm font-medium transition-colors duration-200 ${
                isActive(item.href)
                  ? 'bg-purple-600/20 text-purple-400 border-r-2 border-purple-400'
                  : 'text-gray-300 hover:text-purple-400 hover:bg-purple-600/10'
              }`}
              onClick={() => setIsSidebarOpen(false)}
            >
              <span className="mr-3 text-lg">{item.icon}</span>
              {item.name}
            </Link>
          ))}
        </nav>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top navigation */}
        <header className="bg-dark-100/80 backdrop-blur-sm border-b border-purple-500/20 px-4 lg:px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-purple-400 hover:bg-purple-600/10"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-white">{user?.username}</p>
                <p className="text-xs text-gray-400 capitalize">{user?.role}</p>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="text-center">
                  <div className="text-green-400 font-semibold">${user?.balance?.toFixed(2) || '0.00'}</div>
                  <div className="text-xs text-gray-400">USD Balance</div>
                </div>
                <div className="text-center">
                  <div className="text-blue-400 font-semibold">{user?.credits || 0}</div>
                  <div className="text-xs text-gray-400">Credits</div>
                </div>
                <button
                  onClick={handleBalanceRefresh}
                  className="text-gray-400 hover:text-white text-sm p-1"
                  title="Refresh balance"
                >
                  🔄
                </button>
              </div>

              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-600/10 rounded-lg transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          {children}
        </main>
      </div>

      {/* Sidebar overlay for mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout;
